import {
  type Entity,
  type PreliminaryPrice,
  type PriceChange,
  type PriceHistory,
  type PriceVenue,
  preliminaryPriceSchema,
  price,
  priceChangeSchema,
  type Resolution,
} from '@api';
import { apiFetch, wretchRequest } from '@terminal/utils/api-fetch/apiFetch';

import { isPriceHistoryV2Enabled } from './price-history-flags';

/**
 * Derive the `/v2` venue params from an entity (trading venue + trading country).
 * Uses `trading_country.long` — it's the always-present field the pricing backend
 * keys on (matching the live-price websocket); `.short` is optional and often
 * empty, which would drop `country` and let the backend default to US.
 */
export const getEntityPriceVenue = (entity: Entity): PriceVenue => ({
  exchange: entity.exchange.code || undefined,
  country: entity.trading_country?.long,
});

/** Append the `/v2` venue params to a query string. No-op for empty values. */
const appendVenueParams = (params: URLSearchParams, venue?: PriceVenue) => {
  if (venue?.exchange) {
    params.append('exchange', venue.exchange);
  }

  if (venue?.country) {
    params.append('country', venue.country);
  }

  return params;
};

export const getPreliminaryPrice = (
  subscribableTicker: string,
  venue?: PriceVenue,
  signal?: AbortSignal
) => {
  const v2Enabled = isPriceHistoryV2Enabled();
  const preliminaryPrice = v2Enabled
    ? price.preliminaryPriceV2
    : price.preliminaryPrice;
  let path = preliminaryPrice.path(subscribableTicker);

  if (v2Enabled) {
    const venueQuery = appendVenueParams(
      new URLSearchParams(),
      venue
    ).toString();

    if (venueQuery) {
      path += `&${venueQuery}`;
    }
  }

  return apiFetch<PreliminaryPrice>(path, {
    schema: preliminaryPriceSchema,
    signal,
  });
};

export type GetPricesProps = {
  ticker?: string;
  entityTag: string;
  resolution: Resolution;
  dateFrom?: string;
  dateTo?: string;
} & PriceVenue;

const buildPricesUrl = ({
  ticker,
  entityTag,
  resolution,
  dateFrom,
  dateTo,
  exchange,
  country,
}: GetPricesProps) => {
  const v2Enabled = isPriceHistoryV2Enabled();
  const prices = v2Enabled ? price.pricesV2 : price.prices;
  const params = new URLSearchParams();

  if (ticker) {
    params.append('ticker', ticker);
  }

  if (dateFrom) {
    params.append('from', dateFrom);
  }

  if (dateTo) {
    params.append('to', dateTo);
  }

  params.append('entity_tag', entityTag);
  params.append('resolution', resolution);

  if (v2Enabled) {
    appendVenueParams(params, { exchange, country });
  }

  return `${prices.path}?${params.toString()}`;
};

export const getPrices = async (props: GetPricesProps) =>
  apiFetch<PriceHistory>(buildPricesUrl(props));

export const fetchPrices = async (props: GetPricesProps) =>
  wretchRequest<PriceHistory>(buildPricesUrl(props));

export interface GetPriceChangeProps extends PriceVenue {
  ticker: string;
  reference: string;
  from: string;
  to: string;
}

export const getPriceChange = async ({
  ticker,
  from,
  to,
  reference,
  exchange,
  country,
}: GetPriceChangeProps) => {
  const v2Enabled = isPriceHistoryV2Enabled();
  const priceChange = v2Enabled ? price.priceChangeV2 : price.priceChange;
  const params = new URLSearchParams({
    ticker,
    from,
    to,
    reference,
  });

  if (v2Enabled) {
    appendVenueParams(params, { exchange, country });
  }

  const priceUrl = `${priceChange.path}?${params.toString()}`;

  return wretchRequest<PriceChange>(priceUrl, {
    schema: priceChangeSchema,
    method: 'get',
  });
};
